import request from '../Requests/request'
import Service from './Service'
import type {
  PinSuggestionsFilters,
  DiscardPinSuggestionBody,
  UpdateHealthUnitPinBody
} from '../Types/Requests/PinSuggestionRequests'
import type {
  PinAppliedResponse,
  PinAuditItem,
  PinSuggestionItem,
  PinSuggestionsListResponse
} from '../Types/Responses/PinSuggestionResponses'

const DEFAULT_ERROR = 'Não foi possível carregar as sugestões de pino.'

/** Só os filtros preenchidos vão para a query string (mesmo padrão de CheckAttemptsService). */
function toQuery(filters: PinSuggestionsFilters): Record<string, string | number> {
  const query: Record<string, string | number> = {}
  for (const [key, value] of Object.entries(filters) as Array<[string, unknown]>) {
    if (value !== undefined && value !== null && value !== '') query[key] = value as string | number
  }
  return query
}

/** Fila "Revisão da Geolocalização (pino)", faixa da unidade e gravação do pino com auditoria. */
export default class HealthUnitPinService extends Service {
  prefix: string

  constructor() {
    super()
    this.prefix = ''
  }

  async listSuggestions(filters: PinSuggestionsFilters): Promise<PinSuggestionsListResponse> {
    try {
      const { data } = await request.get<PinSuggestionsListResponse>(
        '/pin-suggestions',
        toQuery(filters)
      )
      return data
    } catch (e) {
      throw this.handleError(e, DEFAULT_ERROR)
    }
  }

  async countOpen(): Promise<{ open: number }> {
    try {
      const { data } = await request.get<{ open: number }>('/pin-suggestions/count')
      return data
    } catch (e) {
      throw this.handleError(e, DEFAULT_ERROR)
    }
  }

  /** O backend responde 200 com corpo vazio quando a unidade não tem sugestão aberta. */
  async getOpenByUnit(healthUnitId: number): Promise<PinSuggestionItem | null> {
    try {
      const { data } = await request.get<PinSuggestionItem | ''>(
        `/healthUnits/${healthUnitId}/pin-suggestion`
      )
      return data && typeof data === 'object' ? data : null
    } catch (e) {
      throw this.handleError(e, DEFAULT_ERROR)
    }
  }

  async discard(
    healthUnitId: number,
    suggestionId: number,
    body: DiscardPinSuggestionBody
  ): Promise<void> {
    try {
      await request.post(
        `/healthUnits/${healthUnitId}/pin-suggestions/${suggestionId}/discard`,
        body
      )
    } catch (e) {
      throw this.handleError(e, 'Não foi possível descartar a sugestão.')
    }
  }

  async updatePin(
    healthUnitId: number,
    body: UpdateHealthUnitPinBody
  ): Promise<PinAppliedResponse> {
    try {
      const { data } = await request.patch<PinAppliedResponse>(
        `/healthUnits/${healthUnitId}/pin`,
        body
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível salvar o pino da unidade.')
    }
  }

  /** Histórico de alterações do pino da unidade — array puro, da mais recente para a mais antiga. */
  async listAudit(healthUnitId: number, limit = 50): Promise<PinAuditItem[]> {
    try {
      const { data } = await request.get<PinAuditItem[]>(`/healthUnits/${healthUnitId}/pin-audit`, {
        limit
      })
      return Array.isArray(data) ? data : []
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar o histórico do pino.')
    }
  }

  async runJob(): Promise<void> {
    try {
      await request.post('/pin-suggestions/run', {})
    } catch (e) {
      throw this.handleError(e, 'Não foi possível recalcular as sugestões.')
    }
  }
}
